import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, CalendarPlus, Loader2, Plus, X, Users, Clock } from 'lucide-react';
import { listActiveClasses, ActiveClass } from '../../members/api';
import { assignTeacherClass } from '../api';
import { useToast } from '../../../context/ToastContext';
import EmptyState from '../../../components/EmptyState';
import { colorClase } from '../../../utils/classColor';
import { horarioCorto } from '../../../utils/horario';

/**
 * «Asignar clase» desde la ficha del profesor (video Karla 30-jul 19:57: «poder irme a
 * [el profe] y asignárselas desde aquí» en vez de entrar clase por clase).
 *
 * 15-ago-2026: era un campo de búsqueda suelto con un desplegable; ahora es BOTÓN + modal con
 * buscador, igual que «Añadir clase» en la ficha del alumno (Carlos: mismo gesto en los dos sitios).
 * Las clases vigentes son pocas (~60): se cargan una vez y se filtran en el navegador.
 */
export default function AssignClassToTeacher(
  { teacherId, ownClassIds, onAssigned }: { teacherId: number; ownClassIds: number[]; onAssigned: () => void },
) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [all, setAll] = useState<ActiveClass[]>([]);
  const [open, setOpen] = useState(false);
  const [asignando, setAsignando] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { listActiveClasses({ vigentes: true }).then((r) => setAll(r.data)).catch(() => setAll([])); }, []);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  // Una clase tiene UN profesor (Carlos 15-ago): ofrecer las que ya tienen maestro sólo lleva a
  // pisar a alguien sin querer. Se listan únicamente las clases SIN profesor; si no queda ninguna,
  // el modal lo dice y explica qué hacer (quitarle la clase al maestro actual).
  const libres = useMemo(() => {
    const suyas = new Set(ownClassIds);
    return all.filter((c) => !suyas.has(c.class_id) && !c.teacher);
  }, [all, ownClassIds]);
  const results = useMemo(() => {
    const s = norm(q.trim());
    if (!s) return libres;
    return libres.filter((c) => norm(`${c.name} ${c.style || ''}`).includes(s));
  }, [libres, q]);

  async function asignar(c: ActiveClass, replace = false) {
    setAsignando(c.class_id);
    try {
      const r = await assignTeacherClass(teacherId, c.class_id, replace);
      if (r.already) toast.info(`${c.name} ya era suya`);
      else toast.success(`${c.name} asignada`);
      setQ(''); setOpen(false);
      onAssigned();
    } catch (e: any) {
      // 409 = la clase ya tiene otro maestro: se confirma antes de pisarlo.
      const actual = e?.data?.current_teacher || e?.current_teacher;
      if (actual && !replace) {
        if (confirm(`${c.name} la da ${actual}. ¿Reemplazarlo?`)) { await asignar(c, true); return; }
      } else {
        toast.error(e?.message || 'No se pudo asignar');
      }
    } finally { setAsignando(null); }
  }

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
        <Plus size={16} /> Asignar clase
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-surface shadow-xl sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <h3 className="font-display text-lg font-bold text-content">Asignarle una clase</h3>
              <button onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-content-3 transition hover:bg-surface-2 hover:text-content" aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
            <div className="border-b border-line p-3">
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
                <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar clase sin maestro…"
                  className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {results.length === 0 ? (
                <EmptyState compact icon={libres.length === 0 ? Users : Search}
                  title={libres.length === 0 ? 'Todas las clases tienen maestro' : 'Sin resultados'}
                  subtitle={libres.length === 0
                    ? 'Para pasarle una clase a este profesor, primero quítasela al maestro que la da hoy desde la ficha de ese maestro (o desde la propia clase).'
                    : 'Prueba con otro nombre.'} />
              ) : (
                <ul className="space-y-1">
                  {results.map((c) => (
                    <li key={c.class_id}>
                      {/* Mismo gris y franja de color que las tarjetas de la ficha */}
                      <button type="button" onClick={() => asignar(c)} disabled={asignando !== null}
                        className="flex w-full items-center gap-3 overflow-hidden rounded-xl border border-line bg-canvas p-2.5 pl-0 text-left transition hover:border-accent/40 hover:bg-surface-2 disabled:opacity-50">
                        <span className="w-1.5 shrink-0 self-stretch" style={{ background: colorClase(c.color) }} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-content">{c.name}</span>
                          {/* Cupo y hora, no el estilo: casi siempre se llama igual que la clase
                              y salía repetido (Carlos 15-ago). Quién la da sí importa aquí. */}
                          {/* Cupo y hora, no el estilo: casi siempre se llama igual que la clase */}
                          <span className="flex flex-wrap items-center gap-x-2 text-xs text-content-3">
                            <span className="inline-flex items-center gap-1"><Users size={11} /> {c.enrolled ?? 0}{c.capacity ? `/${c.capacity}` : ''}</span>
                            {horarioCorto(c.schedule) && <span className="inline-flex items-center gap-1"><Clock size={11} /> {horarioCorto(c.schedule, ' · ')}</span>}
                          </span>
                        </span>
                        {asignando === c.class_id
                          ? <Loader2 size={16} className="shrink-0 animate-spin text-content-3" />
                          : <CalendarPlus size={16} className="shrink-0 text-accent-ink" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t border-line px-4 py-2 text-xs text-content-3">
              {results.length} clase{results.length === 1 ? '' : 's'} sin maestro
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
